import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { memo } from 'react';
import { twJoin } from 'tailwind-merge';

import CarouselBox from 'components/CarouselBox';
import ErrorBoundary from 'components/ErrorBoundary';
import GenericError from 'components/GenericError';

import { isFirstItem } from 'lib/array';
import { sendEvent } from 'lib/gtag';
import placeholder from 'lib/placeholder';
import { offerQueryKeys, topbarQueryKeys } from 'lib/queryKeys';

import { getTopbarAd } from 'services/ads';
import { getFeaturedOffersList } from 'services/offer';

import HomeOfferCard from './HomeOfferCard';

const FeaturedOffers = ({
  className,
  gaEventCategory,
  serverFeaturedOffers,
}) => {
  const { data: topbarAd } = useQuery({
    queryKey: topbarQueryKeys.bannerAd(),
    queryFn: ({ signal }) => getTopbarAd({ signal }),
    cacheTime: Infinity,
    defaultData: true,
    enabled: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const {
    data: featuredOffers = [],
    isSuccess: isSuccessFeaturedOffers,
    isLoading: isLoadingFeaturedOffers,
  } = useQuery({
    queryKey: offerQueryKeys.listByFeatured(),
    queryFn: ({ signal }) => getFeaturedOffersList({ signal }),
    enabled: false,
    initialData: serverFeaturedOffers,
  });

  const onImageClick = () => {
    sendEvent({
      action: 'product_image_featured_deals',
      category: gaEventCategory,
    });
  };

  const onTitleClick = () => {
    sendEvent({
      action: 'product_title_featured_deals',
      category: gaEventCategory,
    });
  };

  return (
    <>
      <div className={twJoin('flex w-full items-center', className)} />
      {!isLoadingFeaturedOffers &&
        isSuccessFeaturedOffers &&
        featuredOffers.length > 0 && (
          <ErrorBoundary
            fallback={
              <GenericError
                message={placeholder('errors.couldNotLoadRecommendedOffers')}
              />
            }
          >
            {/* Mobile: carrossel original inalterado */}
            <div className="lg:hidden">
              <CarouselBox
                arrowsProps={{
                  className: 'hidden lg:flex xl:hidden',
                  offsetX: { left: '-left-2', right: '-right-2' },
                }}
                carouselProps={{ className: 'pl-2 pb-3' }}
                className="-mx-2 -mt-1"
                dotsProps={{ className: 'xl:hidden' }}
                itemProps={{
                  itemSize: 80,
                  slidesPerView: 1,
                  breakpoints: {
                    md: { slidesPerView: 2, itemSize: 90 },
                  },
                }}
                list={featuredOffers}
                render={(offer, index) => (
                  <HomeOfferCard
                    key={`carousel-${offer.key}-${index}`}
                    className="animate-border-glow"
                    gaEventActionPrefix="featured"
                    gaEventCategory={gaEventCategory}
                    surface="featured"
                    isCarousel
                    {...offer}
                    offerImagePriority={!topbarAd && isFirstItem(index)}
                    onImageClick={onImageClick}
                    onTitleClick={onTitleClick}
                  />
                )}
                dots
                full
                showArrows
              />
            </div>
            {/* Desktop: grid 4 colunas com HomeOfferCard */}
            <div className="hidden lg:mb-4 lg:grid lg:grid-cols-4 lg:gap-4">
              {(featuredOffers.length < 4
                ? [
                    ...featuredOffers,
                    ...featuredOffers,
                    ...featuredOffers,
                    ...featuredOffers,
                  ].slice(0, 4)
                : featuredOffers.slice(0, 4)
              ).map((offer, index) => (
                <HomeOfferCard
                  key={`grid-${offer.key}-${index}`}
                  className="animate-border-glow"
                  gaEventActionPrefix="featured"
                  gaEventCategory={gaEventCategory}
                  surface="featured"
                  {...offer}
                  offerImagePriority={!topbarAd && isFirstItem(index)}
                  position={index}
                  onImageClick={onImageClick}
                  onTitleClick={onTitleClick}
                />
              ))}
            </div>
          </ErrorBoundary>
        )}
    </>
  );
};

FeaturedOffers.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  gaEventCategory: PropTypes.string.isRequired,
  serverFeaturedOffers: PropTypes.arrayOf(
    PropTypes.shape({
      categoryId: PropTypes.number.isRequired,
      categorySlug: PropTypes.string.isRequired,
      key: PropTypes.string.isRequired,
      lastActiveOffer: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      offerComments: PropTypes.number.isRequired,
      offerId: PropTypes.number.isRequired,
      offerIsHighlight: PropTypes.bool.isRequired,
      ratings: PropTypes.shape().isRequired,
      offerOldPrice: PropTypes.number,
      offerPhoto: PropTypes.string.isRequired,
      offerPrice: PropTypes.number.isRequired,
      offerPriceType: PropTypes.string.isRequired,
      offerPublished: PropTypes.string.isRequired,
      offerSlug: PropTypes.string.isRequired,
      offerStatusName: PropTypes.string.isRequired,
      offerUserVisibility: PropTypes.string.isRequired,
      offerTags: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          type: PropTypes.string.isRequired,
        })
      ),
      offerTitle: PropTypes.string.isRequired,
      storeDomain: PropTypes.string.isRequired,
      storeId: PropTypes.number,
      storeName: PropTypes.string,
      subcategoryId: PropTypes.number,
      subcategorySlug: PropTypes.string.isRequired,
      userId: PropTypes.number.isRequired,
      userLevel: PropTypes.number,
      userName: PropTypes.string.isRequired,
      userPhoto: PropTypes.string.isRequired,
      userTypeName: PropTypes.string.isRequired,
      userUsername: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default memo(FeaturedOffers);
